import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

import { t } from '@lib/translation';

import {
  IConfirmDialogButtonConfig,
  IConfirmDialogConfig,
} from '../../interfaces/confirm-dialog-config.interfaces';

@Component({
  selector: 'sn-confirm-dialog',
  templateUrl: './confirm-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SnConfirmDialogComponent {
  public enteredConfirmationText: string;
  public confirmationText: string = 'DELETE';
  public confirmationLabel: string;
  public confirmationPlaceholder: string;

  public readonly title: string;
  public readonly message: string;

  public readonly confirmButton: IConfirmDialogButtonConfig;
  public readonly cancelButton: IConfirmDialogButtonConfig;
  public readonly alternativeButton: IConfirmDialogButtonConfig;

  constructor(
    private readonly _matDialogRef: MatDialogRef<SnConfirmDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public config: IConfirmDialogConfig,
    private readonly _sanitizer: DomSanitizer,

  ) {
    this.title = config.title || 'common.confirm';
    this.message = config.message;

    // Handle confirmation input configuration
    if (config.confirmationInput) {
      this.confirmationText = config.confirmationInput.text;
    }

    // Set the label and placeholder based on current confirmationText value
    this.confirmationLabel =
      config.confirmationInput?.label || 
      t('map.confirmDialog.defaultLabel', { text: this.confirmationText });
    this.confirmationPlaceholder =
      config.confirmationInput?.placeholder || 
      t('map.confirmDialog.defaultPlaceholder', { text: this.confirmationText });

    this.confirmButton = this._initConfirmButton(config.confirmButton);
    this.cancelButton = this._initCancelButton(config.cancelButton);
    this.alternativeButton = this._initAlternativeButton(config.alternativeButton);
  }

  public sanitizeHTML(value: any): SafeHtml {
    return this._sanitizer.bypassSecurityTrustHtml(value);
  }

  public confirm(): void {
    this._matDialogRef.close(true);
  }

  public alternative(): void {
    this._matDialogRef.close(this.alternativeButton.value);
  }

  public cancel(): void {
    this._matDialogRef.close(false);
  }

  private _initConfirmButton(
    config?: string | IConfirmDialogButtonConfig,
  ): IConfirmDialogButtonConfig {
    if (!config) {
      return { text: 'common.confirm', color: 'success' };
    } else if (typeof config === 'string') {
      return { text: config, color: 'success' };
    }

    return config;
  }

  private _initAlternativeButton(
    config?: string | IConfirmDialogButtonConfig,
  ): IConfirmDialogButtonConfig {
    if (typeof config === 'string') {
      return { text: config, color: 'accent' };
    }

    return config;
  }

  private _initCancelButton(
    config?: string | IConfirmDialogButtonConfig,
  ): IConfirmDialogButtonConfig {
    if (!config) {
      return { text: 'common.cancel' };
    } else if (typeof config === 'string') {
      return { text: config };
    }

    return config;
  }

  public isConfirmEnabled(): boolean {
    if (this.config.typeDeleteToConfirm || this.config.confirmationInput) {
      return this.enteredConfirmationText === this.confirmationText;
    }
    return true;
  }
}
